import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {
  AutoModelTier,
  AutoTierId,
  AutoTierOption,
} from '@github-ui/copilot-shared-ui/components/chat-model-picker/ModelPickerDesign'
import type {ConfigSchema, JsonPrimitive, ModelSelection, RootState, SessionState} from '@microsoft/agent-host-protocol'

import {AUTO_MODEL_ID} from '../task/ModelPicker'

export const DEFAULT_AUTO_MODEL_TIER: AutoModelTier = 'balance'

const AUTO_TIER_FAILURE_META_KEY = 'copilot.autoTierSwitchFailure'
const AUTO_TIER_REQUEST_VALUES = new Set(['default', 'efficiency', 'balance', 'intelligence', 'fast'])
const SELECTABLE_AUTO_TIERS = new Set<AutoModelTier>(['efficiency', 'balance', 'intelligence'])

export interface AutoTierSwitchFailure {
  requestedAutoTier: string
  effectiveAutoTier: string
  reason: string
  eventId: string
}

export function isSelectableAutoTier(value: unknown): value is AutoModelTier {
  return typeof value === 'string' && SELECTABLE_AUTO_TIERS.has(value as AutoModelTier)
}

function hasConfigProperty(schema: ConfigSchema | undefined, property: string): boolean {
  return Object.hasOwn(schema?.properties ?? {}, property)
}

export function supportsAutoTier(
  sessionState: SessionState | undefined,
  autoModelConfigSchema?: ConfigSchema,
): boolean {
  return (
    hasConfigProperty(sessionState?.config?.schema, 'effectiveAutoTier') ||
    hasConfigProperty(autoModelConfigSchema, 'autoTier')
  )
}

export function readAutoModelConfigSchema(rootState: RootState | undefined): ConfigSchema | undefined {
  for (const agent of rootState?.agents ?? []) {
    const schema = agent.models.find(model => model.id === AUTO_MODEL_ID)?.configSchema
    if (schema) return schema
  }
  return undefined
}

export function readEffectiveAutoTier(sessionState: SessionState | undefined): string | undefined {
  const value = sessionState?.config?.values.effectiveAutoTier
  return typeof value === 'string' ? value : undefined
}

export function readAutoTierSwitchFailure(sessionState: SessionState | undefined): AutoTierSwitchFailure | undefined {
  const value = sessionState?._meta?.[AUTO_TIER_FAILURE_META_KEY]
  if (!value || typeof value !== 'object') return undefined

  const {requestedAutoTier, effectiveAutoTier, reason, eventId} = value as Record<string, unknown>
  if (
    typeof requestedAutoTier !== 'string' ||
    typeof effectiveAutoTier !== 'string' ||
    typeof reason !== 'string' ||
    typeof eventId !== 'string' ||
    eventId.length === 0
  ) {
    return undefined
  }

  return {requestedAutoTier, effectiveAutoTier, reason, eventId}
}

interface MergeModelSelectionConfigOptions {
  modelId: string
  baseModel?: ModelSelection
  updates?: Record<string, JsonPrimitive | undefined>
  removeKeys?: readonly string[]
}

export function mergeModelSelectionConfig({
  modelId,
  baseModel,
  updates = {},
  removeKeys = [],
}: MergeModelSelectionConfigOptions): ModelSelection {
  const config = baseModel?.id === modelId ? {...baseModel.config} : {}

  for (const key of removeKeys) delete config[key]
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) delete config[key]
    else config[key] = value
  }

  return {
    id: modelId,
    ...(Object.keys(config).length > 0 ? {config} : {}),
  }
}

export function sanitizeAutoTierConfig(
  model: ModelSelection,
  autoTierSupported: boolean,
  selectedAutoTier?: AutoTierId,
  /** Copilot Fusion's server-defined tiers, kept rather than dropped as unknown. */
  serverTiers?: readonly AutoTierOption[],
): ModelSelection {
  if (model.id !== AUTO_MODEL_ID || !autoTierSupported) {
    return mergeModelSelectionConfig({
      modelId: model.id,
      baseModel: model,
      removeKeys: ['autoTier'],
    })
  }

  if (selectedAutoTier) {
    return mergeModelSelectionConfig({
      modelId: model.id,
      baseModel: model,
      updates: {autoTier: selectedAutoTier},
    })
  }

  const existingAutoTier = model.config?.autoTier
  return AUTO_TIER_REQUEST_VALUES.has(String(existingAutoTier)) ||
    serverTiers?.some(tier => tier.id === existingAutoTier)
    ? model
    : mergeModelSelectionConfig({
        modelId: model.id,
        baseModel: model,
        removeKeys: ['autoTier'],
      })
}

export function stripAutoTierFromPrompt(prompt: PreparedPrompt): PreparedPrompt {
  if (!prompt.model?.config || !Object.hasOwn(prompt.model.config, 'autoTier')) return prompt

  return {
    ...prompt,
    model: mergeModelSelectionConfig({
      modelId: prompt.model.id,
      baseModel: prompt.model,
      removeKeys: ['autoTier'],
    }),
  }
}

export function readPromptAutoTier(
  prompt: PreparedPrompt,
  serverTiers?: readonly AutoTierOption[],
): AutoTierId | undefined {
  if (prompt.model?.id !== AUTO_MODEL_ID) return undefined
  const value = prompt.model.config?.autoTier
  if (serverTiers) return serverTiers.find(tier => tier.id === value)?.id
  return isSelectableAutoTier(value) ? value : undefined
}
